import React, { useCallback, useEffect, useState } from "react";
import { Typography, Select, Button, notification, Avatar, Grid, theme, Card, Space, Popconfirm, Badge } from "antd";
import { UserAddOutlined, TeamOutlined, DeleteOutlined, CheckOutlined, CloseOutlined, InboxOutlined, SendOutlined, UsergroupAddOutlined } from "@ant-design/icons";
import { useAuth } from "../context/AuthContext";
import { useFriends, usernatorClient } from "../hooks/useFriends";
import { useFollowGraph } from "../hooks/useFollowGraph";
import { AddFriendRequest, FollowRequestAction, UserData } from "../generated/user_pb";
import type { RpcError } from "grpc-web";
import { EmptyState, ErrorState, LoadingState, PageHeader } from "@dowty/ui";

const { Text } = Typography;
const { useBreakpoint } = Grid;

/** addFriend distinguishes these two cases with machine-checkable prefixes so the UI can say
 *  something useful. Previously both arrived as one opaque AlreadyExists, which is what made
 *  re-following someone with a request outstanding look like an unexplained error. */
function describeFollowError(err: unknown, isGerman: boolean): string {
    const message = (err as RpcError)?.message ?? "";
    if (message.startsWith("ALREADY_FOLLOWING")) return isGerman ? "Du folgst dieser Person bereits." : "You already follow them.";
    if (message.startsWith("FOLLOW_REQUEST_ALREADY_PENDING")) return isGerman ? "Du hast dieser Person bereits eine Anfrage gesendet. Sie wartet noch auf eine Bestätigung." : "You've already sent them a request - it's still waiting for approval.";
    return message || (isGerman ? "Etwas ist schiefgelaufen." : "Something went wrong.");
}

const PersonRow: React.FC<{
    user: UserData.AsObject;
    subtitle: string;
    actions: React.ReactNode;
}> = ({ user, subtitle, actions }) => {
    const { token } = theme.useToken();
    const initial = user.username?.[0]?.toUpperCase() ?? "?";
    const avatarColors = [token.colorPrimary, token.colorSuccess, token.colorWarning, token.colorInfo, token.colorError];
    const color = avatarColors[user.userid % avatarColors.length];
    return (
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: token.borderRadius }}>
            <Avatar size={40} style={{ background: color, fontWeight: 700, fontSize: 16, flexShrink: 0 }}>
                {initial}
            </Avatar>
            <div style={{ flex: 1, minWidth: 0 }}>
                <Text strong style={{ display: "block", lineHeight: 1.4, overflowWrap: "anywhere" }}>{user.username}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>{subtitle}</Text>
            </div>
            <Space size={8} wrap style={{ marginLeft: "auto", maxWidth: "100%" }}>{actions}</Space>
        </div>
    );
};

const SectionCard: React.FC<{
    title: React.ReactNode;
    label: string;
    loading?: boolean;
    children: React.ReactNode;
}> = ({ title, label, loading, children }) => {
    const { token } = theme.useToken();
    return (
        <Card
            role="region"
            aria-label={label}
            title={title}
            loading={loading}
            style={{ marginBottom: 16, borderTop: `2px solid ${token.colorPrimaryBorder}` }}
        >
            {children}
        </Card>
    );
};

const renderListState = (
    loading: boolean,
    hasError: boolean,
    isEmpty: boolean,
    loadingLabel: string,
    emptyState: React.ReactNode,
    renderList: () => React.ReactNode,
) => {
    if (loading) return <LoadingState variant="list" label={loadingLabel} />;
    if (hasError && isEmpty) return null;
    if (isEmpty) return emptyState;
    return renderList();
};

const FriendsPage: React.FC = () => {
    const isGerman = navigator.language.toLowerCase().startsWith("de");
    const t = (de: string, en: string) => isGerman ? de : en;
    const { user } = useAuth();
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const { token } = theme.useToken();
    const { friends, loading, error: friendsError, reload: loadFriends } = useFriends();
    const { followers, incoming, outgoing, loading: graphLoading, error: graphError, reload: reloadGraph, buildUserData } = useFollowGraph();

    const [allUsers, setAllUsers] = useState<UserData.AsObject[]>([]);
    const [allUsersError, setAllUsersError] = useState<string | null>(null);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [adding, setAdding] = useState(false);
    const [busyId, setBusyId] = useState<number | null>(null);
    const [api, contextHolder] = notification.useNotification();

    const loadAllUsers = useCallback(async () => {
        if (!user) return;
        setAllUsersError(null);
        try {
            const res = await usernatorClient.getAllUsers(buildUserData(), {});
            setAllUsers(res.getUserList().map((u: UserData) => u.toObject()));
        } catch (err) {
            setAllUsersError((err as Error).message || (isGerman ? "Personen konnten nicht geladen werden." : "Couldn't load people to follow."));
        }
    }, [user, buildUserData, isGerman]);

    useEffect(() => { void loadAllUsers(); }, [loadAllUsers]);

    // Refresh everything after any action, so the four lists never disagree with each other -
    // approving an incoming request, for instance, both empties that row and adds a follower.
    const refreshAll = useCallback(async () => {
        await Promise.all([loadFriends(), reloadGraph()]);
    }, [loadFriends, reloadGraph]);

    const followingIds = new Set(friends.map(f => f.userid));
    const outgoingIds = new Set(outgoing.map(u => u.userid));

    // Exclude both people already followed AND people with a request outstanding. Omitting the
    // second set is what previously let someone re-request and hit an unexplained error.
    const selectOptions = allUsers
        .filter(u => u.userid !== user!.userId && !followingIds.has(u.userid) && !outgoingIds.has(u.userid))
        .map(u => ({ value: u.userid, label: u.username }));

    const run = async (id: number, action: () => Promise<unknown>, successMessage: string, failureTitle: string) => {
        setBusyId(id);
        try {
            await action();
            await refreshAll();
            api.success({ message: successMessage, placement: "top" });
        } catch (err) {
            api.error({ message: failureTitle, description: describeFollowError(err, isGerman), placement: "top" });
        } finally {
            setBusyId(null);
        }
    };

    const followRequest = (targetId: number) => {
        const req = new AddFriendRequest();
        req.setUser(buildUserData());
        req.setTobefriendid(targetId);
        return req;
    };

    const requestAction = (requesterId: number) => {
        const req = new FollowRequestAction();
        req.setUser(buildUserData());
        req.setRequesterid(requesterId);
        return req;
    };

    const handleFollowSelected = async () => {
        if (selectedId == null) return;
        setAdding(true);
        try {
            await usernatorClient.addFriend(followRequest(selectedId), {});
            setSelectedId(null);
            await refreshAll();
            // Whether this became a follow or a pending request depends on the other person's
            // privacy setting, which we don't know here - so don't claim one outcome.
            api.success({ message: t("Gesendet.", "Sent."), description: t("Wenn die Person Anfragen bestätigen muss, wartet deine Anfrage noch. Andernfalls folgst du ihr jetzt.", "If they approve requests, it's waiting for them. Otherwise you're following them now."), placement: "top" });
        } catch (err) {
            api.error({ message: t("Konnte der Person nicht folgen.", "Couldn't follow them."), description: describeFollowError(err, isGerman), placement: "top" });
        } finally {
            setAdding(false);
        }
    };

    return (
        <div className="sf-content-column sf-people" style={{ padding: isMobile ? "16px" : "32px 24px" }}>
            {contextHolder}

            <PageHeader title={t("Personen", "People")} subtitle={t(`${friends.length} gefolgt · ${followers.length} Follower`, `Following ${friends.length} · ${followers.length} ${followers.length === 1 ? "follower" : "followers"}`)} />
            {friendsError && <ErrorState message={friendsError} retry={refreshAll} />}
            {graphError && <ErrorState message={graphError} retry={refreshAll} />}
            {allUsersError && <ErrorState message={allUsersError} retry={loadAllUsers} retryLabel={isGerman ? "Erneut versuchen" : "Retry"} />}

            {/* Incoming: people asking to follow me. Only appears for accounts set to Approval. */}
            {incoming.length > 0 && (
                <SectionCard
                    label={isGerman ? "Eingehende Anfragen" : "Incoming follow requests"}
                    title={<Space><InboxOutlined style={{ color: token.colorPrimary }} /><span>{t("Anfragen, dir zu folgen", "Requests to follow you")}</span><Badge count={incoming.length} /></Space>}
                    loading={graphLoading}
                >
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {incoming.map(person => (
                            <PersonRow
                                key={person.userid}
                                user={person}
                                subtitle={t("möchte dir folgen", "wants to follow you")}
                                actions={
                                    <>
                                        <Button
                                            type="primary" icon={<CheckOutlined />}
                                            loading={busyId === person.userid}
                                            disabled={busyId !== null && busyId !== person.userid}
                                            onClick={() => run(person.userid, () => usernatorClient.approveFollowRequest(requestAction(person.userid), {}), t("Anfrage bestätigt.", "Request approved."), t("Anfrage konnte nicht bestätigt werden.", "Couldn't approve the request."))}
                                        >
                                            {t("Bestätigen", "Approve")}
                                        </Button>
                                        <Button
                                            danger icon={<CloseOutlined />}
                                            loading={busyId === person.userid}
                                            disabled={busyId !== null && busyId !== person.userid}
                                            onClick={() => run(person.userid, () => usernatorClient.rejectFollowRequest(requestAction(person.userid), {}), t("Anfrage abgelehnt.", "Request rejected."), t("Anfrage konnte nicht abgelehnt werden.", "Couldn't reject the request."))}
                                        >
                                            {t("Ablehnen", "Reject")}
                                        </Button>
                                    </>
                                }
                            />
                        ))}
                    </div>
                </SectionCard>
            )}

            {/* Outgoing: requests I've sent that are still waiting. Previously invisible entirely. */}
            {outgoing.length > 0 && (
                <SectionCard
                    label={isGerman ? "Gesendete Anfragen" : "Outgoing follow requests"}
                    title={<Space><SendOutlined style={{ color: token.colorPrimary }} /><span>{t("Gesendete Anfragen", "Requests you've sent")}</span></Space>}
                    loading={graphLoading}
                >
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {outgoing.map(person => (
                            <PersonRow
                                key={person.userid}
                                user={person}
                                subtitle={t("wartet auf Bestätigung", "waiting for them to approve")}
                                actions={
                                    <Button
                                        loading={busyId === person.userid}
                                        disabled={busyId !== null && busyId !== person.userid}
                                        onClick={() => run(person.userid, () => usernatorClient.cancelFollowRequest(followRequest(person.userid), {}), t("Anfrage zurückgezogen.", "Request withdrawn."), t("Anfrage konnte nicht zurückgezogen werden.", "Couldn't withdraw the request."))}
                                    >
                                        {t("Zurückziehen", "Withdraw")}
                                    </Button>
                                }
                            />
                        ))}
                    </div>
                </SectionCard>
            )}

            <SectionCard label={t("Person folgen", "Follow someone")} title={<Space><UserAddOutlined style={{ color: token.colorPrimary }} /><span>{t("Person folgen", "Follow someone")}</span></Space>}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    <Select
                        className="sf-touch-target"
                        aria-label={isGerman ? "Nach Benutzernamen suchen" : "Search by username"}
                        showSearch
                        style={{ flex: "1 1 180px", minWidth: 0 }}
                        placeholder={t("Nach Benutzernamen suchen", "Search by username")}
                        optionFilterProp="label"
                        options={selectOptions}
                        value={selectedId}
                        onChange={setSelectedId}
                        allowClear
                    />
                    <Button style={{ flex: "1 1 96px" }} type="primary" icon={<UserAddOutlined />} loading={adding} disabled={selectedId == null} onClick={handleFollowSelected}>
                        {t("Folgen", "Follow")}
                    </Button>
                </div>
                <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
                    {t("Personen, denen du bereits folgst oder denen du schon eine Anfrage gesendet hast, werden hier nicht angezeigt.", "People you already follow, and anyone with a request outstanding, aren't listed here.")}
                </Text>
            </SectionCard>

            {/* Who I follow */}
            <SectionCard label={t("Gefolgte Personen", "Following")} title={<Space><TeamOutlined style={{ color: token.colorPrimary }} /><span>{t("Gefolgt", "Following")}</span></Space>}>
                {renderListState(loading, Boolean(friendsError), friends.length === 0, t("Gefolgte Personen werden geladen", "Loading people you follow"), <EmptyState icon={<TeamOutlined />} title={t("Du folgst noch niemandem.", "You're not following anyone yet.")} />, () => (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {friends.map(person => (
                            <PersonRow
                                key={person.userid}
                                user={person}
                                subtitle={t("du folgst dieser Person", "you follow them")}
                                actions={
                                    <Button
                                        className="sf-person-delete" type="text" icon={<DeleteOutlined />} aria-label={t(`${person.username} nicht mehr folgen`, `Unfollow ${person.username}`)} style={{ color: token.colorTextSecondary }}
                                        loading={busyId === person.userid}
                                        disabled={busyId !== null && busyId !== person.userid}
                                        onClick={() => run(person.userid, () => usernatorClient.removeFriend(followRequest(person.userid), {}), t("Du folgst der Person nicht mehr.", "Unfollowed."), t("Konnte der Person nicht entfolgen.", "Couldn't unfollow."))}
                                    />
                                }
                            />
                        ))}
                    </div>
                ))}
            </SectionCard>

            {/* Who follows me */}
            <SectionCard label={t("Follower", "Followers")} title={<Space><UsergroupAddOutlined style={{ color: token.colorPrimary }} /><span>{t("Follower", "Followers")}</span></Space>}>
                {renderListState(graphLoading, Boolean(graphError), followers.length === 0, t("Follower werden geladen", "Loading followers"), <EmptyState title={t("Dir folgt noch niemand.", "Nobody follows you yet.")} />, () => (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {followers.map(person => {
                            const iFollowThem = followingIds.has(person.userid);
                            const requested = outgoingIds.has(person.userid);
                            return (
                                <PersonRow
                                    key={person.userid}
                                    user={person}
                                    subtitle={iFollowThem ? t("ihr folgt euch gegenseitig", "you follow each other") : t("folgt dir", "follows you")}
                                    actions={
                                        <>
                                            {!iFollowThem && (
                                                <Button
                                                    type="primary"
                                                    loading={busyId === person.userid}
                                                    disabled={requested || (busyId !== null && busyId !== person.userid)}
                                                    onClick={() => run(person.userid, () => usernatorClient.addFriend(followRequest(person.userid), {}), t("Gesendet.", "Sent."), t("Konnte der Person nicht zurückfolgen.", "Couldn't follow them back."))}
                                                >
                                                    {requested ? t("Angefragt", "Requested") : t("Zurückfolgen", "Follow back")}
                                                </Button>
                                            )}
                                            <Popconfirm
                                                title={t("Diesen Follower entfernen?", "Remove this follower?")}
                                                description={t("Diese Person sieht deine Aktualisierungen dann nicht mehr. Sie kann dir aber erneut folgen.", "They'll stop seeing your updates. It doesn't stop them following you again.")}
                                                okText={t("Entfernen", "Remove")}
                                                okButtonProps={{ danger: true }}
                                                cancelText={t("Abbrechen", "Cancel")}
                                                onConfirm={() => run(person.userid, () => usernatorClient.removeFollower(followRequest(person.userid), {}), t("Follower entfernt.", "Follower removed."), t("Follower konnte nicht entfernt werden.", "Couldn't remove the follower."))}
                                            >
                                                <Button
                                                    className="sf-person-delete" type="text" icon={<DeleteOutlined />} aria-label={t(`${person.username} als Follower entfernen`, `Remove ${person.username} as a follower`)} style={{ color: token.colorTextSecondary }}
                                                    disabled={busyId !== null && busyId !== person.userid}
                                                />
                                            </Popconfirm>
                                        </>
                                    }
                                />
                            );
                        })}
                    </div>
                ))}
            </SectionCard>
        </div>
    );
};

export default FriendsPage;
